import { Grid, GridProps } from '@/components/Grid'
import React from 'react'
import MealOfDayDivisionCreate from './MealOfDayDivisionCreate'
import { getMealOfDayDivisionName } from '@/utils'
import { MealDivisionTemp, MealOfDay, MealOfDayDivision, MealOfDayDivisionType } from '@/api/types'
import ComponentIcon from '@/features/Icons/ComponentIcon'
import SoupIcon from '@/features/Icons/SoupIcon'
import CarrotIcon from '@/features/Icons/CarrotIcon'
import { SORTED_MEALS } from '../constants'
import { Button } from '@/components/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import ReadableStringUtils from '@/features/utils/ReadableStringUtils'
import { useTranslation } from 'react-i18next'

let mealDivisionId = 1000

function generateMealDivisionIdDefault(): number {
  return mealDivisionId++
}
export type GenerateMealDivisionIdFn = () => number | null

function MealDivisionTypeIcon({ type }: { type: MealOfDayDivisionType }) {
  switch (type) {
    case 'MEAL_PART':
      return <ComponentIcon />
    case 'INGREDIENT':
      return <CarrotIcon />
    case 'RECIPE':
      return <SoupIcon />
  }
}

type GripProps = Pick<GridProps, 'columns' | 'gap' | 'style'>

type Props = {
  mealsOfDay: MealOfDay[]
  isReadOnly?: boolean
  onChange?: (mealOfDayDivisions: MealDivisionTemp[]) => void
  mealOfDayDivisions: MealDivisionTemp[]
  generateMealDivisionId?: GenerateMealDivisionIdFn
} & GripProps

export default function SelectedMealDivisions({
  mealsOfDay,
  isReadOnly = false,
  onChange,
  mealOfDayDivisions,
  generateMealDivisionId = generateMealDivisionIdDefault,
  ...gridProps
}: Props) {
  const { t } = useTranslation()

  function handleRemove(mealOfDay: MealOfDay, index: number): void {
    let position = 0
    for (let i = 0; i < mealOfDayDivisions.length; i++) {
      if (mealOfDayDivisions[i].mealOfDay === mealOfDay) {
        if (position++ === index) {
          mealOfDayDivisions.splice(i, 1)
          !isReadOnly && onChange?.(mealOfDayDivisions)
          break
        }
      }
    }
  }

  return (
    <Grid columns="1fr 1fr 1fr" gap={10} {...gridProps}>
      {mealsOfDay.map((mealOfDay) => (
        <div key={mealOfDay}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <div>{ReadableStringUtils.readableMealOfDay(mealOfDay)}</div>
            {!isReadOnly && (
              <MealOfDayDivisionCreate
                onAdd={(mealOfDayDivision) => {
                  !isReadOnly &&
                    onChange?.(
                      [
                        ...mealOfDayDivisions,
                        {
                          ...mealOfDayDivision,
                          mealOfDay,
                          idMealOfDayDivision: generateMealDivisionId(),
                        },
                      ].sort(
                        (a, b) =>
                          SORTED_MEALS.indexOf(a.mealOfDay) - SORTED_MEALS.indexOf(b.mealOfDay),
                      ),
                    )
                }}
              />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {mealOfDayDivisions
              .filter((mod) => mod.mealOfDay === mealOfDay)
              .map((mod, index) => (
                <div
                  key={index + mod.type + getMealOfDayDivisionName(mod)}
                  className="MealPreferencesControl-mealOfDayDivision">
                  <div>
                    <MealDivisionTypeIcon type={mod.type} />
                    <span style={{ marginLeft: '5px' }}>{getMealOfDayDivisionName(mod, t)}</span>
                  </div>
                  {!isReadOnly && (
                    <Button
                      variant="icon"
                      onPress={() => handleRemove(mod.mealOfDay, index)}
                      style={{ padding: 0 }}>
                      <div className="delete-button">
                        <FontAwesomeIcon icon={faTrashAlt} color="var(--red-200)" size="sm" />
                      </div>
                    </Button>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}
    </Grid>
  )
}
